const lusReviewGallerySlot = document.querySelector('[data-lus-full-reviews-gallery]');
const lusProductVideoGallery = document.querySelector('lus-video-gallery');
if (lusReviewGallerySlot && lusProductVideoGallery && lusProductVideoGallery.parentElement !== lusReviewGallerySlot) {
  lusReviewGallerySlot.appendChild(lusProductVideoGallery);
}

if (!customElements.get('lus-video-gallery')) {
  customElements.define('lus-video-gallery', class extends HTMLElement {
    constructor() {
      super();
      this.ratios = new Map();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-lus-film-track]');
      this.cards = [...this.querySelectorAll('[data-lus-film-card]')];
      this.videos = [...this.querySelectorAll('[data-lus-film-video]')];
      this.controls = this.querySelector('[data-lus-film-controls]');
      this.previous = this.querySelector('[data-lus-film-previous]');
      this.next = this.querySelector('[data-lus-film-next]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.videos.forEach(video => {
        video.controls = this.reducedMotion || !this.autoplay;
        video.addEventListener('playing', () => video.closest('.lus-film-card__frame')?.classList.add('is-playing'));
        video.addEventListener('pause', () => video.closest('.lus-film-card__frame')?.classList.remove('is-playing'));
      });

      this.nearObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.hydrate(entry.target.querySelector('[data-lus-film-video]'));
        });
      }, { rootMargin: '400px 15%', threshold: 0.01 });

      this.visibilityObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => this.ratios.set(entry.target, entry.intersectionRatio));
        this.refreshPlayback();
      }, { threshold: [0, 0.25, 0.5, 0.65, 0.8, 1] });

      this.cards.forEach(card => {
        this.nearObserver.observe(card);
        this.visibilityObserver.observe(card);
      });

      this.querySelectorAll('[data-lus-film-sound]').forEach(button => {
        button.addEventListener('click', () => this.toggleSound(button));
      });

      this.previous?.addEventListener('click', () => this.scrollByPage(-1));
      this.next?.addEventListener('click', () => this.scrollByPage(1));
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.track?.addEventListener('mousedown', this.onMouseDown);
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      if (this.track) this.resizeObserver.observe(this.track);
      this.updateControls();
    }

    disconnectedCallback() {
      this.nearObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.dragFrame);
      cancelAnimationFrame(this.scrollAnimationFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('mousedown', this.onMouseDown);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    hydrate(video) {
      if (!video || video.dataset.hydrated === 'true') return;
      video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.hydrated = 'true';
      video.load();
    }

    refreshPlayback() {
      if (!this.autoplay || this.reducedMotion || document.hidden) {
        this.videos.forEach(video => video.pause());
        return;
      }

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      let activeCard = null;

      if (isMobile) {
        activeCard = this.cards.reduce((best, card) => {
          if (!best) return card;
          return (this.ratios.get(card) || 0) > (this.ratios.get(best) || 0) ? card : best;
        }, null);
      }

      this.cards.forEach(card => {
        const video = card.querySelector('[data-lus-film-video]');
        if (!video) return;
        const ratio = this.ratios.get(card) || 0;
        const shouldPlay = isMobile ? card === activeCard && ratio >= 0.55 : ratio >= 0.55;
        if (shouldPlay) {
          this.hydrate(video);
          video.play().catch(() => {});
        } else {
          video.pause();
          if (!video.muted) this.setMuted(video, true);
        }
      });
    }

    toggleSound(button) {
      const video = button.closest('[data-lus-film-card]')?.querySelector('[data-lus-film-video]');
      if (!video) return;
      this.hydrate(video);
      const enableSound = video.muted;
      this.videos.forEach(other => {
        if (other !== video) this.setMuted(other, true);
      });
      this.setMuted(video, !enableSound);
      if (enableSound && video.paused) video.play().catch(() => {});
    }

    setMuted(video, muted) {
      video.muted = muted;
      const button = video.closest('[data-lus-film-card]')?.querySelector('[data-lus-film-sound]');
      if (!button) return;
      button.setAttribute('aria-pressed', String(!muted));
      const label = video.getAttribute('aria-label') || 'video';
      button.setAttribute('aria-label', `${muted ? 'Enable' : 'Mute'} sound for ${label}`);
    }

    scrollByPage(direction) {
      if (!this.track) return;
      const firstCard = this.cards[0];
      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const step = (firstCard?.getBoundingClientRect().width || this.track.clientWidth * 0.82) + gap;
      const visibleCards = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.animateScrollTo(this.track.scrollLeft + direction * step * visibleCards);
    }

    animateScrollTo(requestedLeft) {
      if (!this.track) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const target = Math.max(0, Math.min(maximum, requestedLeft));
      const start = this.track.scrollLeft;
      const distance = target - start;

      if (this.reducedMotion || Math.abs(distance) < 1) {
        this.track.scrollLeft = target;
        return;
      }

      cancelAnimationFrame(this.scrollAnimationFrame);
      const startedAt = performance.now();
      const duration = Math.min(650, Math.max(420, Math.abs(distance) * 0.42));
      this.track.classList.add('is-programmatic-scrolling');

      const animate = now => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.track.scrollLeft = start + distance * eased;
        if (progress < 1) {
          this.scrollAnimationFrame = requestAnimationFrame(animate);
          return;
        }
        this.track.classList.remove('is-programmatic-scrolling');
        this.scrollAnimationFrame = null;
        this.updateControls();
      };

      this.scrollAnimationFrame = requestAnimationFrame(animate);
    }

    onMouseDown(event) {
      if (event.button !== 0 || !this.track) return;
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.track.classList.remove('is-programmatic-scrolling');
      event.preventDefault();
      this.dragStartX = event.clientX;
      this.dragCurrentX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.isDragging = true;
      this.track.classList.add('is-dragging');
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp, { once: true });
    }

    onMouseMove(event) {
      if (!this.isDragging || !this.track) return;
      event.preventDefault();
      this.dragCurrentX = event.clientX;
      if (this.dragFrame) return;
      this.dragFrame = requestAnimationFrame(() => {
        this.dragFrame = null;
        if (!this.isDragging || !this.track) return;
        this.track.scrollLeft = this.dragStartScroll - (this.dragCurrentX - this.dragStartX);
      });
    }

    onMouseUp() {
      if (!this.isDragging || !this.track) return;
      if (this.dragFrame) {
        cancelAnimationFrame(this.dragFrame);
        this.dragFrame = null;
        this.track.scrollLeft = this.dragStartScroll - (this.dragCurrentX - this.dragStartX);
      }
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      window.removeEventListener('mousemove', this.onMouseMove);
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateControls();
      });
    }

    onResize() {
      this.updateControls();
      this.refreshPlayback();
    }

    onVisibilityChange() {
      this.refreshPlayback();
    }

    onBlockSelect(event) {
      const card = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
      card?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    }

    updateControls() {
      if (!this.track || !this.controls) return;
      const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
      this.controls.hidden = !hasOverflow;
      if (!hasOverflow) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }
  });
}
